import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { burnInConfigs } from './BurnInAnalyticsPages';

const getApiBase = () =>
  process.env.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:3001' : '');

const formatDateParam = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return value.toISOString().split('T')[0];
};

function useBurnInWidgetData(endpoint, { measuredDateStart = '', measuredDateEnd = '' } = {}) {
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let active = true;

    const run = async () => {
      setState({ loading: true, error: '', data: null });
      try {
        const response = await axios.get(`${getApiBase()}${endpoint}`, {
          withCredentials: true,
          params: {
            measuredDateStart,
            measuredDateEnd,
          },
        });
        if (!active) return;
        setState({ loading: false, error: '', data: response.data });
      } catch (error) {
        if (!active) return;
        setState({
          loading: false,
          error: error?.message || 'Failed to load burn-in widget data.',
          data: null,
        });
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [endpoint, measuredDateEnd, measuredDateStart]);

  return state;
}

function WidgetState({ styles, message }) {
  return <div className={styles.placeholderContent}>{message}</div>;
}

function WidgetStats({ styles, stats }) {
  return (
    <div className={styles.widgetMiniStats}>
      {stats.filter(Boolean).map((stat) => (
        <div className={styles.widgetMiniStat} key={stat.label}>
          <span>{stat.label}</span>
          <strong>{stat.value}</strong>
        </div>
      ))}
    </div>
  );
}

function WidgetFooter({ styles, navigate, path, label = 'Open full page' }) {
  return (
    <div className={styles.widgetLinkRow}>
      <button type="button" className={styles.widgetLinkButton} onClick={() => navigate(path)}>
        {label}
      </button>
    </div>
  );
}

function renderChart(chartType, chartData, styles) {
  if (!chartData) return <WidgetState styles={styles} message="No chart preview is available." />;
  const common = { data: chartData, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } } };

  if (chartType === 'pie') {
    return <Pie {...common} />;
  }
  if (chartType === 'doughnut') {
    return <Doughnut {...common} />;
  }
  if (chartType === 'line') {
    return <Line {...common} />;
  }
  return <Bar {...common} />;
}

export default function BurnInDashboardWidgetBody({
  configPath,
  styles,
  navigate,
  path,
  startDate,
  endDate,
}) {
  const { t } = useTranslation();
  const config = burnInConfigs[configPath];
  const measuredDateStart = useMemo(() => formatDateParam(startDate), [startDate]);
  const measuredDateEnd = useMemo(() => formatDateParam(endDate), [endDate]);
  const { loading, error, data } = useBurnInWidgetData(config?.endpoint, {
    measuredDateStart,
    measuredDateEnd,
  });

  const rows = useMemo(() => {
    if (!config) return [];
    if (typeof config.getRows === 'function') {
      const nextRows = config.getRows(data);
      return Array.isArray(nextRows) ? nextRows : [];
    }
    return Array.isArray(data) ? data : [];
  }, [config, data]);

  const chartData = useMemo(() => {
    if (!config) return null;
    if (loading) return null;
    if (typeof config.buildChartData === 'function') {
      const needsFullPayload = config.chartType === 'doughnut' || config.chartType === 'pie' || configPath === '/burn-in-status';
      if (!needsFullPayload && (!rows || rows.length === 0)) {
        return null;
      }
      return needsFullPayload
        ? config.buildChartData(data)
        : config.buildChartData(rows);
    }
    return null;
  }, [config, configPath, data, loading, rows]);

  const metrics = useMemo(() => {
    if (!config?.getMetrics) return [];
    const useFullPayloadMetrics = configPath === '/burn-in-status' || config.chartType === 'pie';
    return (useFullPayloadMetrics || config?.customWidget === 'temperatureAnalyzer')
      ? config.getMetrics(config?.customWidget === 'temperatureAnalyzer'
        ? (Array.isArray(data?.stepSensitivity) ? data.stepSensitivity : [])
        : data)
      : config.getMetrics(rows);
  }, [config, configPath, data, rows]);

  const temperatureAnalyzerPreview = useMemo(() => {
    if (config?.customWidget !== 'temperatureAnalyzer') return null;
    const stepSensitivity = Array.isArray(data?.stepSensitivity) ? data.stepSensitivity : [];
    const stepAnalysis = Array.isArray(data?.stepAnalysis) ? data.stepAnalysis : [];
    const summary = data?.summary || {};

    const topSteps = stepSensitivity
      .filter((row) => Number(row.total_fails) > 0)
      .slice(0, 3)
      .map((row, index) => {
        const key = `${row.sequence_text}|||${row.step_name}`;
        const temperatures = stepAnalysis
          .filter((stepRow) => `${stepRow.sequence_text}|||${stepRow.step_name}` === key)
          .sort((left, right) => Number(left.temperature_bucket) - Number(right.temperature_bucket));

        return {
          ...row,
          rank: index + 1,
          key,
          temperatures,
        };
      });

    return {
      summary,
      topSteps,
      chartData: {
        labels: topSteps.map((row) => `${row.sequence_text} · ${row.step_name}`),
        datasets: [
          {
            label: t('FAIL-rate spread %'),
            data: topSteps.map((row) => Number(row.fail_rate_spread) || 0),
            backgroundColor: ['#dc2626', '#f97316', '#7c3aed'],
            borderRadius: 8,
          },
        ],
      },
    };
  }, [config?.customWidget, data, t]);

  const temperatureAnalyzerChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t('FAIL-rate spread %'),
        },
      },
    },
  }), [t]);

  const renderTemperatureAnalyzerWidget = () => {
    const topSteps = temperatureAnalyzerPreview?.topSteps || [];
    const summary = temperatureAnalyzerPreview?.summary || {};

    if (!topSteps.length) {
      return <WidgetState styles={styles} message={t('No critical temperature-sensitive burn-in steps were found yet.')} />;
    }

    return (
      <>
        <WidgetStats
          styles={styles}
          stats={[
            { label: t('Critical steps'), value: topSteps.length },
            { label: t('Worst temp'), value: summary?.worst_temperature_label || '-' },
            { label: t('Temp points'), value: summary?.temperature_point_count ?? 0 },
            { label: t('Fail temps'), value: summary?.fail_temperature_point_count ?? 0 },
          ]}
        />
        <div className={styles.widgetChartWide}>
          <Bar data={temperatureAnalyzerPreview.chartData} options={temperatureAnalyzerChartOptions} />
        </div>
        <div className={styles.temperatureWidgetGrid}>
          {topSteps.map((step) => (
            <article key={step.key} className={styles.temperatureWidgetCard}>
              <div className={styles.temperatureWidgetHeader}>
                <span className={styles.temperatureWidgetEyebrow}>{t('Critical step')} #{step.rank}</span>
                <strong className={styles.temperatureWidgetTitle}>{step.sequence_text} · {step.step_name}</strong>
                <p className={styles.temperatureWidgetSubtle}>
                  {step.header_sequence ? `${step.header_sequence} · ` : ''}{step.header_name || t('Burn-in thermal step')}
                </p>
              </div>
              <div className={styles.temperatureWidgetStats}>
                <span>{t('FAILs')}: <strong>{step.total_fails}</strong></span>
                <span>{t('Spread')}: <strong>{Number(step.fail_rate_spread || 0).toFixed(2)}%</strong></span>
                <span>{t('Temps seen')}: <strong>{step.temperature_count}</strong></span>
                <span>{t('Worst temp')}: <strong>{step.worst_temperature_label || '-'}</strong></span>
              </div>
              <div className={styles.temperatureWidgetRows}>
                {step.temperatures.map((temperatureRow) => {
                  const isWorst = temperatureRow.temperature_label === step.worst_temperature_label;
                  return (
                    <div
                      key={`${step.key}-${temperatureRow.temperature_bucket}`}
                      className={`${styles.temperatureWidgetRow} ${isWorst ? styles.temperatureWidgetRowCritical : ''}`}
                    >
                      <span>{temperatureRow.temperature_label}</span>
                      <span>
                        {Number(temperatureRow.fail_rate || 0).toFixed(2)}% FAIL
                        {' · '}
                        {temperatureRow.fail_rows}/{temperatureRow.total_rows}
                      </span>
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
        <p className={styles.widgetCaption}>
          {t('This preview mirrors the first three critical steps from the manual step selector and summarizes how each one reacts across the available chamber temperatures.')}
        </p>
        <WidgetFooter styles={styles} navigate={navigate} path={path} />
      </>
    );
  };

  if (!config) return <WidgetState styles={styles} message="This burn-in widget is not available yet." />;
  if (loading) return <WidgetState styles={styles} message="Loading burn-in widget..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (config?.customWidget === 'temperatureAnalyzer') {
    return renderTemperatureAnalyzerWidget();
  }
  if ((!rows || rows.length === 0) && configPath !== '/burn-in-status') {
    return <WidgetState styles={styles} message="No burn-in data matched this widget yet." />;
  }

  return (
    <>
      <WidgetStats styles={styles} stats={metrics} />
      <div className={config.chartType === 'doughnut' || config.chartType === 'pie' ? styles.widgetChartTall : styles.widgetChartWide}>
        {renderChart(config.chartType, chartData, styles)}
      </div>
      <p className={styles.widgetCaption}>{config.widgetDescription || config.description}</p>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}
